import {Service} from '@s-ui/domain'

export class GetPageTrackingDataService extends Service {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Mapper} deps.fromPageNameToConstantNamingPageName
   * @param {Mapper} deps.trackingProxyMapper
   * @param {Object} deps.config
   * @param {Service} deps.getCurrentUserService
   * @param {Service} deps.getIdentifyTrackingDataService
   * @param {Service} deps.getResetTrackingDataService
   * @param {ValueObject} deps.valueObject
   */
  constructor({
    config,
    getCurrentUserService,
    trackingProxyMapper,
    fromPageNameToConstantNamingPageName,
    getResetTrackingDataService,
    getIdentifyTrackingDataService,
    valueObject
  }) {
    super()

    this._config = config
    this._defaultCulture = config.get('DEFAULT_CULTURE')

    this._fromPageNameToConstantNamingPageName = fromPageNameToConstantNamingPageName
    this._getCurrentUserService = getCurrentUserService
    this._getIdentifyTrackingDataService = getIdentifyTrackingDataService
    this._getResetTrackingDataService = getResetTrackingDataService
    this._trackingProxyMapper = trackingProxyMapper
    this._valueObject = valueObject
  }

  /**
   * @param {Object} params
   * @param {Array} params.ad.highlightType
   * @param {Array} params.advertisements
   * @param {Array} params.merchantList
   * @param {Array} params.similarAds
   * @param {Array} params.vehicles
   * @param {Boolean} params.ad.vehicleHistory
   * @param {Number} params.ad.id
   * @param {Number} params.ad.price
   * @param {Number} params.httpStatusCode
   * @param {Number} params.pageNumber
   * @param {Object} params.ad
   * @param {String} params.city
   * @param {Object} params.filters
   * @param {String} params.formName
   * @param {Object} params.instantOffer
   * @param {Object} params.lastFilter
   * @param {Object} params.search
   * @param {String} params.bodyType
   * @param {String} params.category
   * @param {String} params.contactType
   * @param {String} params.culture
   * @param {String} params.event
   * @param {String} params.make
   * @param {Number} params.makeId
   * @param {String} params.model
   * @param {Number} params.modelId
   * @param {String} params.origin
   * @param {String} params.page
   * @param {String} params.pageName
   * @param {String} params.pageOrigin
   * @param {String} params.pageType
   * @param {String} params.plateVehicleSelected
   * @param {String} params.province
   * @param {String} params.referer
   * @param {String} params.searchParams
   * @param {String} params.searchResultsCount
   * @param {String} params.searchTerm
   * @param {String} params.section
   * @param {String} params.step
   * @param {String} params.tag
   * @param {String} params.title
   * @param {String} params.url
   * @param {String} params.version
   * @param {String} params.withPlateStatus
   * @param {String} params.zipcode
   * @return {Promise<Object, Error>} This is a Promise return value.
   */
  async execute({
    ad,
    adDetail,
    advertisements,
    bodyType,
    category,
    category2,
    city,
    color,
    contactType,
    culture = this._defaultCulture,
    dealerId,
    errorCode,
    event,
    filters,
    financingInstalmentPrice,
    formName,
    fuelType,
    httpStatusCode,
    instantOffer,
    isRetry,
    lastFilter,
    listingSectionId,
    listType,
    make,
    makeId,
    merchantList,
    model,
    modelId,
    origin,
    page,
    pageName,
    pageNumber,
    pageOrigin,
    pageType,
    plateVehicleSelected,
    province,
    products,
    ratings,
    referer,
    search,
    searchParams,
    searchResultsCount,
    searchTerm,
    section,
    similarAds,
    step,
    tab,
    tag,
    title,
    trimLevel,
    type,
    url,
    vehicles,
    version,
    withPlateStatus,
    zipcode
  }) {
    const userEntity = await this._getCurrentUserService.execute()
    const user = userEntity?.toJSON?.()
    const domainPageName = this._fromPageNameToConstantNamingPageName.map(page)
    const domainPageNameOrigin = this._fromPageNameToConstantNamingPageName.map(pageOrigin)

    const trackingMapper = this._trackingProxyMapper.createFor({
      culture,
      page: domainPageName,
      user
    })

    let trackingData = trackingMapper({
      ad,
      adDetail,
      advertisements,
      bodyType,
      category,
      category2,
      city,
      color,
      contactType,
      dealerId,
      errorCode,
      event,
      filters,
      financingInstalmentPrice,
      formName,
      fuelType,
      httpStatusCode,
      instantOffer,
      isRetry,
      lastFilter,
      listingSectionId,
      listType,
      make,
      makeId,
      merchantList,
      model,
      modelId,
      origin,
      pageName,
      pageNumber,
      pageOrigin: domainPageNameOrigin,
      pageType,
      plateVehicleSelected,
      products,
      province,
      ratings,
      referer,
      search,
      searchParams,
      searchResultsCount,
      searchTerm,
      section,
      similarAds,
      step,
      tab,
      tag,
      title,
      trimLevel,
      type,
      url,
      vehicles,
      version,
      withPlateStatus,
      zipcode
    })

    if (!trackingData) return

    const trackingEvent = event || trackingData.getEvent()

    const reset = await this._getResetTrackingDataService.execute()

    trackingData = this._valueObject({
      ...trackingData.toJSON(),
      reset
    })

    const identify = await this._getIdentifyTrackingDataService.execute({
      event: trackingEvent
    })

    if (identify?.userId) {
      // Unwrap tracking data value object in order to wrap
      // it again as a valid value object with new params (identify).
      trackingData = this._valueObject({
        ...trackingData.toJSON(),
        identify
      })
    }
    return trackingData.toJSON()
  }
}
